import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter, HostListener, Inject,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges, ViewChild
} from "@angular/core";
import * as _ from "lodash";
import {PlanningViewSettingsRoutesModel} from "../../../models/planning-view-settings.model";
import {DEFAULT_ROUTES_COLUMN_WIDTHS} from "../../../constants/planning-column-widths.constant";
import {ROUTES_COLUMN_GROUPS} from "../../../constants/planning-column-groups.constant";
import {PlanningDragAndDropService} from "../../services/planning-drag-and-drop.service";
import {TableColumnSettings} from "../../../../commons/angular/services/table-column-manager";
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
} from "@angular/cdk/drag-drop";
import {PlanningPanelModel} from "../../../models/planning-panel.model";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {PlanningRouteModel} from "../../../models/planning-route.model";
import {PlanningResourceModel} from "../../../../resources/models/planning-resource.model";
import {Address} from "../../../../addresses/models/address.model";

require('./planning-draft-routes-table-list.scss');
@Component({
  standalone: false,
  selector: 'app-planning-draft-routes-table-list',
  templateUrl: './planning-draft-routes-table-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningDraftRoutesTableListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() routes: PlanningRouteModel[];
  @Input() allJobs: any[] = [];
  @Input() resources: any[];
  @Input() draft: any;
  @Input() panel: PlanningPanelModel;
  @Input() panels: PlanningPanelModel[];
  @Input() jobs: any[];
  @Input() selectedIds: string[] = [];
  @Input() selectedJobIds: string[];
  @Input() visibleIds: string[];
  @Input() settings: PlanningViewSettingsRoutesModel;
  @Input() itemElementSize: string;
  @Input() scrollOffset: number = 0;
  @Input() dropListSortingDisabled: boolean = true;
  @Input() dropListId?: string;
  @Input() emptyDropText?: string;
  @Input() enableJobDropOnRow: boolean = true;

  @Output() onSelectRoutes = new EventEmitter<string[]>();
  @Output() onSelect = new EventEmitter<{ route: PlanningRouteModel, scrollOffset: number, isFloating?: boolean }>();
  @Output() onVisibleRoutesChange = new EventEmitter<string[]>();
  @Output() onLockRouteChange = new EventEmitter<{id: string, locked: boolean}>();
  @Output() onChangeRouteResource = new EventEmitter<{routeId: string, event: any}>();
  @Output() onEditResourceAllocation = new EventEmitter<{resource: PlanningResourceModel, route: PlanningRouteModel, event: any}>();
  @Output() onAddToRoute = new EventEmitter();
  @Output() onDrillDown = new EventEmitter<{routeId: string, index?: number, event: any}>();
  @Output() onDispatch = new EventEmitter<string>();
  @Output() onReoptimize = new EventEmitter<string>();
  @Output() onPreAssignAgent = new EventEmitter<string>();
  @Output() onPrint = new EventEmitter<string>();
  @Output() onPrintLoadingSheet = new EventEmitter<string>();
  @Output() onDelete = new EventEmitter<string>();
  @Output() onFeedback = new EventEmitter<{ routeId: string, feedback: string }>();
  @Output() onListDrop = new EventEmitter<CdkDragDrop<any>>();
  @Output() onSettingsChange = new EventEmitter<PlanningViewSettingsRoutesModel>();

  get uniqueId(): string {
    return 'routes-' + this.panel?.id;
  }
  defaultWidths: { [key: string]: number } = DEFAULT_ROUTES_COLUMN_WIDTHS;
  columnGroups = ROUTES_COLUMN_GROUPS;

  @ViewChild('headerContainer', {static: false}) private headerContainer: ElementRef;
  @ViewChild('bodyContainer', {read: ElementRef, static: false}) private bodyContainer: ElementRef;
  @ViewChild(CdkVirtualScrollViewport, {static: false}) private viewport: CdkVirtualScrollViewport;

  private resizeObserver: ResizeObserver;
  private scrollEndTimer: any | null = null;
  private clickTimeout: any | null = null;

  public connectedPanelIds: string[];
  public selectedKeys: { [key: string]: boolean } = {};
  public visibleKeys: { [key: string]: boolean } = {};
  public hovered: boolean = false;
  public isShowCopyBtn: boolean = false;
  public dropAnimations: Array<{x: number, y: number, text: string, id: number, isMultiple: boolean, count: number}> = [];
  public weightUnit: string = this.splitService.getShortWeightUnit();
  public volumeUnit: string = this.splitService.getVolumeUnit();
  public severityThresholds: { low: number; mid: number; } = { low: 80, mid: 99 };
  public routeUiDataMap: { [routeId: string]: any } = {};
  public logisticsVehicleNameByResourceId: { [resourceId: string]: string } = {};

  constructor(
    @Inject('confirmDialogService') private confirmDialogService,
    @Inject('planningAlertsService') private planningAlertsService,
    @Inject('planningDialogService') private planningDialogService,
    @Inject('planningCommonService') private planningCommonService,
    @Inject('splitService') private splitService,
    @Inject('$timeout') private $timeout,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(PlanningDragAndDropService) private planningDragAndDropService: PlanningDragAndDropService
    ) {
  }

  ngOnInit() {
    this.resizeObserver = new ResizeObserver(() => {
      this.viewport?.checkViewportSize();
      this.syncHeaderScrollbarWidth();
    });
  }

  ngAfterViewInit() {
    if (this.bodyContainer) {
      this.resizeObserver.observe(this.bodyContainer.nativeElement);

      const innerWrapper = this.bodyContainer.nativeElement.querySelector('.table-inner-wrapper');
      if (innerWrapper) {
        this.resizeObserver.observe(innerWrapper);
      }
    }
    this.syncHeaderScrollbarWidth();

    if (this.viewport && this.scrollOffset) {
      this.$timeout(() => this.viewport.scrollToOffset(this.scrollOffset), 0);
    }

  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.panels || changes.resources || changes.routes) {
      const resourceIds = _.map(this.resources, 'id');
      const panelIds = _.map(this.panels, 'id');
      const routeIds = _.map(this.routes, 'id');
      this.connectedPanelIds = _.uniq(_.concat(panelIds || [], resourceIds || [], routeIds || []));
    }
    if (changes.resources || changes.routes) {
      this.logisticsVehicleNameByResourceId = _.mapValues(_.keyBy(this.resources, 'id'), 'mainLogisticsVehicleReference.name');
    }
    if (changes.selectedIds && !this.selectedIds) { this.selectedIds = []; }
    if (changes.selectedIds) {
      this.selectedKeys = {};
      _.forEach(this.selectedIds, (id) => {
        this.selectedKeys[id] = true;
      });
    }
    if (changes.visibleIds) {
      this.visibleKeys = {};
      _.forEach(this.visibleIds, (id) => {
        this.visibleKeys[id] = true;
      });
    }

    if (changes.routes && this.routes?.length) {
      this.viewport?.checkViewportSize();
    }

    if (changes.routes || changes.draft) {
      this._precomputeRoutesUiData();
    }

    this.cdr.detectChanges();
  }

  private _precomputeRoutesUiData() {
    this.routeUiDataMap = {};
    if (this.routes?.length) {
      this.routes.forEach((route) => {
        const details = this._computeRouteAccountsDetails(route);

        this.routeUiDataMap[route.id] = {
          isRunning: this.getRouteIsRunning(route),
          color: this.getRouteColor(route),
          dateRange: this.getRouteDateRange(route),
          pickingTime: this.getRoutePickingTime(route),
          totalIncomeClass: this.getTotalIncomeClass(route),
          weightSeverity: this.getWeightSeverity(route),
          volumeCapacity: this.getVolumeCapacity(route),
          volumeSeverity: this.getVolumeSeverity(route),
          palletsSeverity: this.getPalletsSeverity(route),
          accountIds: details.map(d => d.id),
          accountsDetails: details,
          accountExternalIds: _.compact(_.uniq(details.map(d => d.externalId)))
        };
      });
    }
  }

  private _computeRouteAccountsDetails(route: PlanningRouteModel): Array<{id: string, name: string, externalId: string, pallets: number}> {
    const accountsDetailsMap = new Map<string, { name: string; externalId: string; pallets: number; address?: Address }>();

    const flatActivities = _.flatMap(route.activities, (activity: any) => {
      if (activity.type === 'START' || activity.type === 'END') {
        return [];
      }
      return activity.type === 'GROUP' ? (activity.stopActivities || []) : [activity];
    });

    const hasDropoffs = _.some(flatActivities, (a: any) => a.stopType === 'Dropoff');
    const sideActivities = hasDropoffs
      ? _.filter(flatActivities, (a: any) => a.stopType === 'Dropoff')
      : _.filter(flatActivities, (a: any) => a.stopType !== 'Dropoff');

    sideActivities.forEach((a: any) => {
      if (!a.accountId) {
        return;
      }

      let accountValue = accountsDetailsMap.get(a.accountId);
      if (!accountValue) {
        accountValue = {name: a.accountName, externalId: a.accountExternalId, pallets: 0, address: a.address};
        accountsDetailsMap.set(a.accountId, accountValue);
      }

      accountValue.pallets = (a.packages || []).reduce((acc, pkg) => acc + (pkg.pallets || 0), accountValue.pallets);
    });

    return Array.from(accountsDetailsMap.entries()).map(([id, val]) => ({
      id,
      name: val.name,
      address: val.address,
      externalId: val.externalId,
      pallets: val.pallets
    }));
  }

  @HostListener('window:resize')
  onResize() {
    this.viewport?.checkViewportSize();
  }

  onBodyScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.headerContainer) {
      this.headerContainer.nativeElement.scrollLeft = target.scrollLeft;
    }
  }

  onScroll(_: Event): void {
    if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => {
      if (this.viewport) {
        this.viewport.checkViewportSize();
      }
      this.scrollEndTimer = null;
    }, 200);
  }

  trackByRouteId(_: number, item: { id: string | number }) {
    return item.id;
  }

  getRouteColor(route: PlanningRouteModel) {
    return this.planningCommonService.getRouteColor(route);
  }

  getRoutePickingTime(route: PlanningRouteModel) {
    const routeStartTime = route.activities?.[0]?.eta || route.activities?.[0]?.notBefore;
    return {
      time: routeStartTime ? new Date(routeStartTime).getTime() - ((route.pickingTimeOffsetInMinutes || 0) * 60000) : undefined
    };
  }

  getRouteDateRange(route: PlanningRouteModel) {
    return {
      notBefore: _.get(_.head(route.activities), 'eta'),
      notAfter: _.get(_.last(route.activities), 'etd')
    };
  }

  getRouteIsRunning(route: PlanningRouteModel) {
    return route.actionProcess?.running && (new Date().getTime() < route.actionProcess?.expirationDate);
  }

  getWeightSeverity(route: PlanningRouteModel): string {
    return this.getSeverity(route.totals?.totalLoad || 0, route.resource?.capacity?.weight);
  }

  getVolumeSeverity(route: PlanningRouteModel): string {
    const volumeCapacity = this.getVolumeCapacity(route);
    return this.getSeverity(route.totals?.totalVolume || 0, volumeCapacity);
  }

  getPalletsSeverity(route: PlanningRouteModel): string {
    return this.getSeverity(route.totals?.totalPallets || 0, route.resource?.capacity?.numberOfPallets);
  }

  getVolumeCapacity(route: PlanningRouteModel) {
    return route.resource.capacity.dimensions ?
      (route.resource.capacity.dimensions.height * route.resource.capacity.dimensions.width * route.resource.capacity.dimensions.length) : 0;
  }

  getSeverity(value: number, capacity?: number): string {
    if (!capacity) {
      return '';
    }
    const ratio = (value / capacity) * 100;
    return (ratio < this.severityThresholds.low) ? 'low' : ((ratio >= this.severityThresholds.low && ratio < this.severityThresholds.mid) ? 'mid' : 'high');
  }

  getTotalIncomeClass(route: PlanningRouteModel) {
    const totalIncome = route.totals?.totalIncome;
    const minimumRouteIncome = route.resource.routeIncomePreferences?.minimumRouteIncome || this.draft?.settings?.incomeSettings?.minRouteIncome;
    const maximumRouteIncome = route.resource.routeIncomePreferences?.maximumRouteIncome || this.draft?.settings?.incomeSettings?.maxRouteIncome;
    return (!!minimumRouteIncome && (totalIncome < minimumRouteIncome)) ? 'low' :
      ((!!maximumRouteIncome && (totalIncome > maximumRouteIncome)) ? 'high' : '');
  }

  onToggleSelectedRoute(id, isSelected: boolean) {
    this.selectedKeys[id] = isSelected;
    const selectedIds = _.map(this.selectedKeys, (value, key) => {
      if (value) return key;
    });

    this.onSelectRoutes.emit(_.compact(selectedIds));
  }

  onToggleVisibleRoute(id, isVisible: boolean) {
    this.visibleKeys[id] = isVisible;
    const visibleIds = _.map(this.visibleKeys, (value, key) => {
      if (value) return key;
    });

    this.onVisibleRoutesChange.emit(_.compact(visibleIds));
  }

  rowDropEnterPredicate = (drag: CdkDrag<any>, drop: CdkDropList<any>): boolean => {
    return this.enableJobDropOnRow;
  }

  onDrop(route: any, event: CdkDragDrop<any[]>) {
    this.planningDragAndDropService.onDropRoutes(
      route,
      this.draft?.settings.optimizeDispatchedRoutes ?? false,
      event,
      this.selectedJobIds,
      this.allJobs,
      this.panels,
      this.showDropAnimation.bind(this)
    ).then((payload) => {
      if (payload) {
        this.onAddToRoute.emit(payload);
      }
    });
  }

  showDropAnimation(x: number, y: number, count: number, isMultiple: boolean): void {
    const animationId = Date.now();

    this.dropAnimations.push({
      x: x,
      y: y,
      text: isMultiple ? `${count} Jobs` : 'Job',
      id: animationId,
      isMultiple: isMultiple,
      count: count
    });

    this.cdr.detectChanges();

    this.$timeout(() => {
      this.dropAnimations = this.dropAnimations.filter(a => a.id !== animationId);
      this.cdr.detectChanges();
    }, 600);
  }

  onEditResourceAllocationClick(route: PlanningRouteModel, event: any) {
    const resource = this.resources.find((r) => r.id === route.resource.id);
    this.onEditResourceAllocation.emit({resource: resource, route: route, event: event});
  }

  onChangeRouteResourceClick(routeId: string, event: any) {
    this.onChangeRouteResource.emit({routeId: routeId, event: event});
  }

  onRowClick(route: PlanningRouteModel, scrollOffset: number) {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }
    this.clickTimeout = setTimeout(() => {
      this.onSelect.emit({ route: route, scrollOffset: scrollOffset, isFloating: false });
      this.clickTimeout = null;
    }, 250);
  }

  onRowDoubleClick(route: PlanningRouteModel, scrollOffset: number) {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }
    this.onSelect.emit({ route: route, scrollOffset: scrollOffset, isFloating: true });
  }

  private syncHeaderScrollbarWidth() {
    if (this.headerContainer && this.bodyContainer) {
      const bodyEl = this.bodyContainer.nativeElement;
      const headerEl = this.headerContainer.nativeElement;
      const scrollbarWidth = bodyEl.offsetWidth - bodyEl.clientWidth;
      headerEl.style.paddingRight = `${scrollbarWidth}px`;
    }
  }
}
